import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'

import IconButton from 'LuxKit/Button/IconButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { trackRoomAvailabilityStripDateSelected, trackRoomAvailabilityStripImpression } from 'analytics/snowplow/events/roomAvailabilityStripEvents'
import Clickable from 'components/Common/Clickable'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatDateTimeRange from 'components/utils/Formatters/FormatDateTimeRange'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getCalendarDateStringInTimeZone } from 'lib/datetime/dateUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'

import useRoomAlternativeStayOptions from './useRoomAlternativeStayOptions'

/** Number of alternative check-in dates shown per page */
const DATES_PER_PAGE = 7
/** Days before the selected check-in the strip starts from (keeps the selection mid-strip) */
const LEAD_IN_DAYS = 3
/** How far past the selected check-in alternative dates are offered */
const ALTERNATIVE_WINDOW_DAYS = 30

const Container = styled(Group)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-top: 0;
  padding: ${rem(28)} ${rem(36)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

/**
 * Positioned relatively so the nav buttons can float above the row's edges without
 * consuming layout width — the row must stay the same width whether or not either
 * button is rendered, otherwise the cells resize/overflow on every page change.
 */
const StripWrapper = styled.div`
  position: relative;
`

const StripRow = styled.div`
  display: flex;
  align-items: stretch;
  overflow-x: auto;
`

const DateCell = styled(Clickable)`
  position: relative;
  flex: 1 1 0;
  min-width: ${rem(96)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(4)};
  padding: ${rem(12)} ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  text-align: center;

  &:not(:first-child) {
    margin-inline-start: -1px;
  }

  &.sold-out {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    cursor: default;
  }

  &.selected {
    z-index: 1;
    border: 2px solid ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  &:hover:not(.sold-out):not(.selected) {
    border-color: ${props => props.theme.palette.neutral.default.four};
  }
`

const NavButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  z-index: 2;
  background-color: ${props => props.theme.palette.neutral.surface.staticWhite};
  box-shadow: ${props => props.theme.shadow.flat.large};

  &.previous {
    inset-inline-start: 0;
    transform: translate(-50%, -50%);

    [dir='rtl'] & {
      transform: translate(50%, -50%);
    }
  }

  &.next {
    inset-inline-end: 0;
    transform: translate(50%, -50%);

    [dir='rtl'] & {
      transform: translate(-50%, -50%);
    }
  }
`

interface Props {
  propertyId: string
  propertyRoomId: string
  propertyTimezone: string
  checkIn: string
  duration: number
  occupancies: Array<App.Occupants>
}

function PropertyRoomAvailabilityStrip(props: Props) {
  const {
    propertyId,
    propertyRoomId,
    propertyTimezone,
    checkIn,
    duration,
    occupancies,
  } = props

  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()
  const [pageOffset, setPageOffset] = useState(0)
  // A fresh instance id per distinct "search" the strip renders for — the full accommodation
  // context is already attached to the event separately, so this only needs to identify this
  // specific rendering, not carry business keys itself.
  const listInstance = useMemo(() => uuidV4(),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [propertyId, propertyRoomId, checkIn, duration, occupancies])

  const [impressionRef, isInView] = useInView({ threshold: 0.5, triggerOnce: true })
  useEffect(() => {
    if (isInView) {
      trackRoomAvailabilityStripImpression(listInstance)
    }
  }, [isInView, listInstance])

  // Grounded in the property's own timezone (not the browser's) so a customer and a property on
  // opposite sides of a date boundary agree on what "today" is.
  const today = getCalendarDateStringInTimeZone(new Date(), propertyTimezone)
  // The strip anchors on `checkIn - LEAD_IN_DAYS` so the user's selected dates sit mid-strip, but
  // dates before today are never valid, so pageOffset can still page back further towards today
  // whenever there's room (see `minPageOffset`). ISO dates sort lexicographically, so these stay
  // as plain strings and compare with `<=`/`>=` rather than needing moment comparisons.
  const windowStart = moment.max(
    moment(checkIn).subtract(LEAD_IN_DAYS, 'days'),
    moment(today),
  ).format(ISO_DATE_FORMAT)
  const maxCheckIn = moment(checkIn).add(ALTERNATIVE_WINDOW_DAYS, 'days').format(ISO_DATE_FORMAT)
  // Backward paging is capped to the same ALTERNATIVE_WINDOW_DAYS as forward paging, floored at
  // today, so picking a check-in far in the future doesn't let you page all the way back to today.
  const minCheckIn = moment.max(
    moment(checkIn).subtract(ALTERNATIVE_WINDOW_DAYS, 'days'),
    moment(today),
  ).format(ISO_DATE_FORMAT)
  const minPageOffset = -moment(windowStart).diff(moment(minCheckIn), 'days')

  const pageDates = useMemo(() => {
    const start = moment(windowStart).add(pageOffset, 'days')
    return Array.from({ length: DATES_PER_PAGE }, (_, index) => {
      const date = moment(start).add(index, 'days')
      return {
        checkIn: date.format(ISO_DATE_FORMAT),
        checkOut: moment(date).add(duration, 'days').format(ISO_DATE_FORMAT),
      }
    }).filter(candidate => candidate.checkIn <= maxCheckIn)
  }, [windowStart, pageOffset, duration, maxCheckIn])

  const stayOptions = useRoomAlternativeStayOptions(
    propertyId,
    propertyRoomId,
    pageDates,
    occupancies,
  )

  const hasPreviousPage = pageOffset > minPageOffset
  const hasNextPage = moment(windowStart).add(pageOffset + DATES_PER_PAGE, 'days').format(ISO_DATE_FORMAT) <= maxCheckIn

  const onPreviousPage = useCallback(() => {
    setPageOffset(offset => Math.max(minPageOffset, offset - DATES_PER_PAGE))
  }, [minPageOffset])
  const onNextPage = useCallback(() => {
    setPageOffset(offset => offset + DATES_PER_PAGE)
  }, [])

  const heading = intl.formatMessage({
    id: 'IHoTvL3jXabJhKT9yqw2',
    defaultMessage: 'Room availability',
    description: 'Heading above the strip of alternative check-in dates shown when a room is sold out on the selected dates',
  })
  const soldOutLabel = intl.formatMessage({
    id: 'PS3bzjUBOVRm-td8UgLH',
    defaultMessage: 'Sold out',
    description: 'Label inside an alternative date cell when the room has no availability for that stay',
  })
  const unavailableLabel = intl.formatMessage({
    id: 'wbT9AYpyDJ-PomydzEj6',
    defaultMessage: 'Unavailable',
    description: 'Label inside an alternative date cell when its availability could not be checked due to a fetch error',
  })
  const previousLabel = intl.formatMessage({
    id: 'VuzEznFJyuIYC1pt1t2N',
    defaultMessage: 'Previous 7 days',
    description: 'Pagination control that shows the previous seven alternative check-in dates in the room availability strip',
  })
  const nextLabel = intl.formatMessage({
    id: 'T0VXaNYs3LSq0jkWRU0x',
    defaultMessage: 'Next 7 days',
    description: 'Pagination control that shows the next seven alternative check-in dates in the room availability strip',
  })

  return (
    <Container ref={impressionRef} direction="vertical" gap={12} data-testid="room-availability-strip">
      <BodyText variant="large" colour="neutral-two">{heading}</BodyText>
      <StripWrapper>
        <StripRow>
          {pageDates.map(candidate => {
            const stayOption = stayOptions[candidate.checkIn] ?? { status: 'loading' as const }
            const isAvailable = stayOption.status === 'available'
            const isUnavailable = stayOption.status === 'sold-out' || stayOption.status === 'error'
            const isSelected = candidate.checkIn === checkIn
            // Days from the customer's original check-in, not the cell's position within the
            // currently visible page — otherwise paging forward and picking the first cell would
            // always report position 1 regardless of how far the customer actually paged.
            const positionFromOriginalCheckIn = moment(candidate.checkIn).diff(moment(checkIn), 'days')
            return (
              <DateCell
                key={candidate.checkIn}
                className={clsx({ 'sold-out': isUnavailable, selected: isSelected })}
                disabled={!isAvailable || isSelected}
                onClick={() => {
                  trackRoomAvailabilityStripDateSelected(listInstance, positionFromOriginalCheckIn)
                  offerPageDispatch({ type: OfferPageActions.setDates, checkIn: candidate.checkIn, checkOut: candidate.checkOut })
                }}
                data-testid={`room-availability-strip-cell-${candidate.checkIn}`}
              >
                <Caption variant="medium" colour="light-foreground-alt">
                  <FormatDateTimeRange
                    startValue={candidate.checkIn}
                    endValue={candidate.checkOut}
                    day="numeric"
                    month="short"
                  />
                </Caption>
                {stayOption.status === 'loading' && <TextLoadingBox width="10ch" />}
                {stayOption.status === 'available' && (
                  <PriceRowPrice size="S" price={stayOption.totalPrice} saleUnit="total" />
                )}
                {stayOption.status === 'sold-out' && <BodyText variant="medium" weight="bold" format="uppercase" colour="neutral-one">
                  {soldOutLabel}
                </BodyText>}
                {stayOption.status === 'error' && <BodyText variant="medium" colour="light-foreground-alt">
                  {unavailableLabel}
                </BodyText>}
              </DateCell>
            )
          })}
        </StripRow>
        {/* Figma's mobile frame has no chevron controls — pagination there is via the text links
          below only, so the tile row can be swiped natively without also jumping a whole page. */}
        <CSSBreakpoint min="tablet">
          {hasPreviousPage && <NavButton
            className="previous"
            kind="secondary"
            shape="circle"
            aria-label={previousLabel}
            onClick={onPreviousPage}
          >
            <LineArrowLeftIcon />
          </NavButton>}
          {hasNextPage && <NavButton
            className="next"
            kind="secondary"
            shape="circle"
            aria-label={nextLabel}
            onClick={onNextPage}
          >
            <LineArrowRightIcon />
          </NavButton>}
        </CSSBreakpoint>
      </StripWrapper>
      <Group direction="horizontal" horizontalAlign="space-between">
        {hasPreviousPage ? <TextLink size="medium" onClick={onPreviousPage}>{previousLabel}</TextLink> : <span />}
        {hasNextPage && <TextLink size="medium" onClick={onNextPage}>{nextLabel}</TextLink>}
      </Group>
    </Container>
  )
}

export default withOfferPageAndReduxState((state) => ({
  occupancies: state.rooms,
}), PropertyRoomAvailabilityStrip)
